"use client";

import React, { useRef, useState } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ShieldCheck, ArrowRight, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import posthog from "posthog-js";

gsap.registerPlugin(ScrollTrigger);

const BookDemoSection = () => {
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    city: "",
    interest: "RWA Solutions",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // PostHog: Track demo form submission attempt
      posthog.capture("demo_form_submitted", {
        name: formData.name,
        email: formData.email,
        city: formData.city,
        interest: formData.interest,
        has_mobile: !!formData.mobile,
      });

      // Send to API
      const response = await fetch("/api/send-demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send request");
      }

      // Success: Show toast and reset form
      toast.success("Request Sent!", {
        description: "We'll get back to you within 24 hours.",
        duration: 5000,
      });

      // PostHog: Track successful submission
      posthog.capture("demo_form_success", {
        email: formData.email,
        interest: formData.interest,
      });

      // Reset form
      setFormData({
        name: "",
        mobile: "",
        email: "",
        city: "",
        interest: "RWA Solutions",
      });
    } catch (error) {
      // Error: Show toast
      const errorMessage =
        error instanceof Error ? error.message : "Failed to send request";
      toast.error("Failed to send", {
        description: errorMessage,
        duration: 5000,
      });

      // PostHog: Track failed submission
      posthog.capture("demo_form_failed", {
        error: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 70%",
          toggleActions: "play none none reverse",
        },
      });
      tl.from(".demo-text-col", {
        x: -50,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
      })
        .from(
          ".demo-form-card",
          { x: 50, opacity: 0, duration: 0.8, ease: "power3.out" },
          "-=0.6",
        )
        .from(
          ".form-element",
          {
            y: 20,
            opacity: 0,
            duration: 0.5,
            stagger: 0.1,
            ease: "back.out(1.7)",
          },
          "-=0.4",
        );
    },
    { scope: containerRef },
  );

  return (
    <section
      ref={containerRef}
      className="py-24 bg-[#080808] px-4 relative overflow-hidden border-t border-white/5"
    >
      <div className="absolute inset-0 bg-linear-to-br from-[#050505] via-[#0A0A0A] to-[#111]" />
      <div className="absolute top-0 right-0 w-150 h-150 bg-orange-500/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="max-w-6xl mx-auto relative z-10 grid lg:grid-cols-2 gap-16 items-center">
        <div className="demo-text-col">
          <h2 className="text-5xl md:text-6xl text-center md:text-left font-clash font-bold text-white mb-6 leading-tight">
            Experience <br />
            <span className="text-transparent bg-clip-text bg-linear-to-r from-orange-500 to-amber-500">
              Smart Dwara
            </span>
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed max-w-lg mb-8 text-center md:text-left">
            Moved into a new society? RWA member? Want your business on the
            block? Looking to secure your home with a smart device?
          </p>
          <div className="flex gap-4 justify-center md:justify-start">
            <div className="w-12 h-12 rounded-full border border-white/10 bg-white/5 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <h4 className="text-white font-clash font-semibold">
                Bank-Grade Security
              </h4>
              <p className="text-sm text-gray-500">ISO 27001 Certified</p>
            </div>
          </div>
        </div>
        <div className="demo-form-card bg-[#111] border border-white/10 p-8 rounded-4xl shadow-2xl">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 form-element">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Name
                </label>
                <input
                  suppressHydrationWarning
                  type="text"
                  placeholder="Enter Name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-orange-500 transition-colors"
                  required
                />
              </div>
              <div className="space-y-2 form-element">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Mobile
                </label>
                <input
                  suppressHydrationWarning
                  type="tel"
                  placeholder="Enter Mobile Number"
                  value={formData.mobile}
                  onChange={(e) =>
                    setFormData({ ...formData, mobile: e.target.value })
                  }
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-orange-500 transition-colors"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 form-element">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Email ID
                </label>
                <input
                  suppressHydrationWarning
                  type="email"
                  placeholder="Enter Email Address"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-orange-500 transition-colors"
                  required
                />
              </div>
              <div className="space-y-2 form-element">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  City
                </label>
                <input
                  suppressHydrationWarning
                  type="text"
                  placeholder="Enter City"
                  value={formData.city}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-orange-500 transition-colors"
                  required
                />
              </div>
            </div>
            <div className="space-y-2 form-element">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                I am interested in...
              </label>
              <select
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none"
                value={formData.interest}
                onChange={(e) =>
                  setFormData({ ...formData, interest: e.target.value })
                }
              >
                <option>RWA Solutions</option>
                <option>Individual Home Security</option>
                <option>Commercial Access Control</option>
              </select>
            </div>
            <div className="form-element">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 bg-[#F97316] hover:bg-orange-600 disabled:bg-orange-800 disabled:cursor-not-allowed text-black hover:text-white font-clash font-semibold uppercase rounded-xl transition-all shadow-lg shadow-orange-900/20 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Book a demo with us <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Toast Notifications */}
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0A0A0A",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#fff",
          },
        }}
      />
    </section>
  );
};

export default BookDemoSection;
